import { defineStore } from 'pinia'
import type { FetchError } from 'ofetch'
import { useSelectionStore } from '~/store/selection-store'
import { useUserStore } from '~/store/user-store'
import { useApi } from '~/composables/api'
import type { PriceList } from '~/types/PriceList'
import type { Vehicle } from '~/types/Vehicle'
import type { Product } from '~/types/Product'
import type { Store } from '~/types/Store'
import type { Coupon } from '~/types/Coupon'
import type { SubscriptionPlan } from '~/types/SubscriptionPlan'
import type { ItemSubscriptionPlan } from '~/types/ItemSubscriptionPlan'

export const usePriceListStore = defineStore('priceList', () => {
  const {
    fetchWithoutAuth
  } = useApi()

  // ----------------------------------------
  // PRODUCT HELPER
  // ----------------------------------------
  const {
    isProductCompatibleWithVehicle,
    isProductIncludedByDefault,
    isProductOneTime
  } = useProduct()

  // ----------------------------------------
  // TRANSLATION HELPER
  // ----------------------------------------
  const {
    getTranslation
  } = useTranslations()

  // ----------------------------------------
  // SELECTION STORE
  // ----------------------------------------
  const selectionStore = useSelectionStore()

  // ----------------------------------------
  // USER STORE
  // ----------------------------------------
  const userStore = useUserStore()

  const isPriceListLoading = ref<boolean>(false)

  const activePriceList = ref<PriceList | null | undefined>(null)
  const activeCoupons = ref<Array<Coupon>>([])

  // ----------------------------------------
  // PRICE LIST VEHICLES
  // ----------------------------------------
  const vehicles = computed((): Array<Vehicle> => {
    let innerVehicles: Array<Vehicle> = []

    if (!activePriceList.value) {
      return []
    }

    // SET isGroup PROPERTY TO IDENTIFY VEHICLE GROUPS AND MODELS
    if (Array.isArray(activePriceList.value.vehicle_model_groups)) {
      activePriceList.value.vehicle_model_groups.forEach((group: Vehicle) => group.isGroup = true)
      innerVehicles = innerVehicles.concat(activePriceList.value.vehicle_model_groups)
    }

    // SET isGroup PROPERTY TO IDENTIFY VEHICLE GROUPS AND MODELS
    if (Array.isArray(activePriceList.value.vehicle_models)) {
      activePriceList.value.vehicle_models.forEach((model: Vehicle) => model.isGroup = false)
      innerVehicles = innerVehicles.concat(activePriceList.value.vehicle_models)
    }

    return innerVehicles
  })

  const enabledVehicles = computed((): Array<Vehicle> => {
    const innerEnabledVehicles = vehicles.value.filter((vehicle: Vehicle) =>
      vehicle.is_enabled
    )

    const outOfStockVehicles = innerEnabledVehicles.filter((vehicle: Vehicle) =>
      !vehicle.has_stock
    )

    const inStockVehicles = innerEnabledVehicles.filter((vehicle: Vehicle) =>
      vehicle.has_stock
    )

    // Falls back to the default locale (or empty string) when a label is missing for the current locale
    const alphabeticalSort = (a: Vehicle, b: Vehicle): number =>
      String(getTranslation(a.label)).localeCompare(String(getTranslation(b.label)))

    const innerVehicles = [
      ...inStockVehicles.sort(alphabeticalSort),
      ...outOfStockVehicles.sort(alphabeticalSort)
    ]

    innerVehicles.forEach((vehicle: Vehicle) => {
      // Drop item plans whose general plan is missing from the price list; without it
      // the plan has no billing_frequency/label and breaks rendering (e.g. SSR 500)
      vehicle.subscription_plans = vehicle.subscription_plans.filter((plan: ItemSubscriptionPlan) => {
        const generalPlan = subscriptionPlans.value.find((innerGeneralPlan: SubscriptionPlan) =>
          innerGeneralPlan.id === plan.subscription_plan_id
        )

        if (!generalPlan) {
          console.warn(`Subscription plan ${plan.subscription_plan_id} not found in price list; skipping for vehicle ${vehicle.id}`)

          return false
        }

        Object.assign(plan, generalPlan)

        return true
      })
    })

    return innerVehicles
  })

  // ----------------------------------------
  // PRICE LIST PRODUCTS
  // ----------------------------------------
  const products = computed((): Array<Product> => {
    if (!activePriceList.value) {
      return []
    }

    return activePriceList.value.products ?? []
  })

  const enabledProducts = computed((): Array<Product> =>
    products.value.filter((product: Product) =>
      product.is_enabled
    )
  )

  const filteredProducts = computed((): Array<Product> => {
    const innerEnabledProducts = products.value.filter((product: Product) =>
      product.is_enabled
    )

    const innerFilteredProducts = innerEnabledProducts.filter((product: Product) =>
      !isProductOneTime(product) || !isProductIncludedByDefault(product)
    )

    const filteredCompatibleProducts = innerFilteredProducts.filter((product: Product) => {
      if (!selectionStore.selectedVehicle) {
        return false
      }

      return isProductCompatibleWithVehicle(product, selectionStore.selectedVehicle)
    })

    return filteredCompatibleProducts
  })

  // ----------------------------------------
  // PRICE LIST STORE
  // ----------------------------------------
  const store = computed((): Store | undefined => {
    if (!activePriceList.value) {
      return
    }
    return activePriceList.value.store
  })

  // ----------------------------------------
  // PRICE LIST SUBSCRIPTION PLANS
  // ----------------------------------------
  const subscriptionPlans = computed((): Array<SubscriptionPlan> =>
    activePriceList.value?.subscription_plans ?? []
  )

  const subscriptionPlanTypes = computed((): Array<string> => {
    const innerSubscriptionPlanTypes = new Set<string>()

    subscriptionPlans.value.forEach((subscriptionPlan: SubscriptionPlan) => {
      if (subscriptionPlan.type) {
        innerSubscriptionPlanTypes.add(subscriptionPlan.type)
      }
    })

    return Array.from(innerSubscriptionPlanTypes)
  })

  const currentPriceListType = computed((): string | null =>
    subscriptionPlanTypes.value.length === 1 ? subscriptionPlanTypes.value[0] : null
  )

  const relatedAlternativeType = computed((): string | null => {
    if (currentPriceListType.value === 'lease') return 'rent_to_own'
    if (currentPriceListType.value === 'rent_to_own') return 'lease'
    return null
  })

  const relatedAlternativeStoreId = computed((): string | null => {
    if (!store.value) return null
    if (currentPriceListType.value === 'lease') return store.value.related_rent_to_own_store_id ?? null
    if (currentPriceListType.value === 'rent_to_own') return store.value.related_lease_store_id ?? null
    return null
  })

  // ----------------------------------------
  // FETCH ACTIVE PRICE LIST
  // ----------------------------------------
  const getActivePriceList = async function (storeId: string): Promise<{ error: FetchError | undefined } | undefined> {
    const { selectedStore } = storeToRefs(selectionStore)

    const { user } = storeToRefs(userStore)

    isPriceListLoading.value = true

    const { data, error } = await fetchWithoutAuth<PriceList>(`/stores/${storeId}/activePriceList`)

    if (!data || error) {
      isPriceListLoading.value = false

      return { error }
    }

    isPriceListLoading.value = false

    activePriceList.value = data

    selectedStore.value = data.store

    if (typeof data.store.location?.country === 'string' && user.value) {
      user.value.country = data.store.location?.country
    }
  }

  // ----------------------------------------
  // COUPONS
  // ----------------------------------------
  const redeemCoupon = async function ({ couponCode }: { couponCode: string }): Promise<{ error: FetchError | undefined } | undefined> {
    const { selectedStore } = storeToRefs(selectionStore)

    const { data, error } = await fetchWithoutAuth<Coupon>('/coupons/validate', {
      method: 'POST',
      body: {
        store_id: selectedStore.value?.id,
        coupon: couponCode
      }
    })

    if (!data || error) {
      removeCoupon({ couponCode })
      return { error }
    }

    addCoupon(data)
  }

  const addCoupon = (coupon: Coupon): void => {
    const isCouponAlreadyAdded = activeCoupons.value.some((innerCoupon: Coupon) =>
      innerCoupon.code === coupon.code
    )

    if (!isCouponAlreadyAdded) {
      activeCoupons.value.push(coupon)
    }
  }

  const removeCoupon = ({ couponCode }: { couponCode: string }): void => {
    activeCoupons.value = activeCoupons.value.filter((coupon: Coupon) =>
      coupon.code !== couponCode
    )
  }

  return {
    // ----------------------------------------
    // PRICE LIST VEHICLES
    // ----------------------------------------
    enabledVehicles,
    vehicles,

    // ----------------------------------------
    // PRICE LIST PRODUCTS
    // ----------------------------------------
    enabledProducts,
    products,
    filteredProducts,

    // ----------------------------------------
    // PRICE LIST STORE
    // ----------------------------------------
    store,

    // ----------------------------------------
    // COUPONS
    // ----------------------------------------
    activeCoupons,
    redeemCoupon,
    removeCoupon,

    // ----------------------------------------
    // PRICE LIST SUBSCRIPTION PLANS
    // ----------------------------------------
    subscriptionPlans,
    subscriptionPlanTypes,
    currentPriceListType,
    relatedAlternativeType,
    relatedAlternativeStoreId,

    // ----------------------------------------
    // PRICE LIST
    // ----------------------------------------
    getActivePriceList,
    isPriceListLoading,
    activePriceList
  }
})
